// StudioZoom — the Studio's zoom pill (pen "Zoom", "Studio – detail design"
// §1): − / % / + / fit to content. The % opens the zoom menu (Fit to content,
// 50 %, 100 %, 200 %). Fit is `scan`, never a curved arrow (that reads as
// undo). Pure: the zoom comes in, each choice goes out.

export { StudioZoom };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";

const PRESETS: readonly number[] = [0.5, 1, 2];

const percent = (zoom: number): string => `${Math.round(zoom * 100)} %`;

const StudioZoom = ({
    zoom,
    onZoomIn,
    onZoomOut,
    onZoomTo,
    onFit,
}: {
    readonly zoom: number;
    readonly onZoomIn: () => void;
    readonly onZoomOut: () => void;
    readonly onZoomTo: (zoom: number) => void;
    readonly onFit: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const items: readonly MenuItem[] = [
        { _tag: "item", label: t`Fit to content`, onSelect: onFit },
        ...PRESETS.map(
            (z): MenuItem => ({
                _tag: "item",
                label: percent(z),
                checked: Math.abs(z - zoom) < 0.001,
                onSelect: () => onZoomTo(z),
            }),
        ),
    ];
    return (
        <div className="bilda-studio__zoom">
            <IconButton label={t`Zoom out`} size="sm" onClick={onZoomOut}>
                <Icon name="minus" />
            </IconButton>
            <Menu
                side="top"
                items={items}
                trigger={
                    <button
                        type="button"
                        className="bilda-studio__zoom-level"
                        aria-label={t`Zoom: ${percent(zoom)}`}
                    >
                        {percent(zoom)}
                    </button>
                }
            />
            <IconButton label={t`Zoom in`} size="sm" onClick={onZoomIn}>
                <Icon name="plus" />
            </IconButton>
            <span className="bilda-studio__divider" aria-hidden="true" />
            <IconButton label={t`Fit to content`} size="sm" onClick={onFit}>
                <Icon name="scan" />
            </IconButton>
        </div>
    );
};
